import { useEffect, useLayoutEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'

import {
  claimOrdersBadgeAnimation,
  hasPendingOrdersBadgeAnimation,
  ordersCountQueryOptions,
} from 'shared/api/orders-count-query'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'

import s from './styles.module.scss'

interface Props {
  icon: AllIcons
  iconClassName: string
  isAuthenticated: boolean
}

export const OrdersNavigationIcon = ({
  icon,
  iconClassName,
  isAuthenticated,
}: Props) => {
  const { data: ordersCountData } = useQuery({
    ...ordersCountQueryOptions,
    enabled: isAuthenticated,
  })
  const [animationRevision, setAnimationRevision] = useState(0)
  const ordersCount = isAuthenticated ? (ordersCountData?.count ?? 0) : 0
  const growthRevision = isAuthenticated
    ? (ordersCountData?.growthRevision ?? 0)
    : 0
  const [badgeCount, setBadgeCount] = useState(ordersCount)
  const [isBadgeExiting, setIsBadgeExiting] = useState(false)
  const displayedBadgeCount = ordersCount || badgeCount

  useLayoutEffect(() => {
    if (ordersCount > 0) {
      if (badgeCount !== ordersCount) setBadgeCount(ordersCount)
      if (isBadgeExiting) setIsBadgeExiting(false)
      return
    }

    if (!badgeCount) return

    setIsBadgeExiting(true)

    const timeout = window.setTimeout(() => {
      setBadgeCount(0)
      setIsBadgeExiting(false)
    }, 180)

    return () => window.clearTimeout(timeout)
  }, [ordersCount, badgeCount])

  useEffect(() => {
    if (!ordersCount || !hasPendingOrdersBadgeAnimation(growthRevision)) return

    const timeout = window.setTimeout(() => {
      if (claimOrdersBadgeAnimation(growthRevision)) {
        setAnimationRevision(growthRevision)
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [growthRevision, ordersCount])

  useEffect(() => {
    if (!animationRevision) return

    const timeout = window.setTimeout(() => setAnimationRevision(0), 420)

    return () => window.clearTimeout(timeout)
  }, [animationRevision])

  const hasNewOrders = ordersCount > 0
  const showBadge = hasNewOrders || badgeCount > 0
  const isAnimationPending =
    hasNewOrders && hasPendingOrdersBadgeAnimation(growthRevision)
  const isAnimationActive =
    hasNewOrders &&
    animationRevision > 0 &&
    animationRevision === growthRevision
  const isBadgeLeaving = ordersCount === 0 && badgeCount > 0 && isBadgeExiting

  return (
    <>
      <AppIcon
        id={icon}
        className={clsx(iconClassName, isAnimationActive && s.iconAnimated)}
        width={20}
        height={20}
      />

      {showBadge && (
        <span
          key={`orders-badge-${growthRevision}`}
          className={clsx(
            s.ordersBadge,
            isAnimationPending && s.ordersBadgePending,
            isAnimationActive && s.ordersBadgeAnimated,
            isBadgeLeaving && s.ordersBadgeExiting,
          )}
        >
          {displayedBadgeCount > 9 ? '9+' : displayedBadgeCount}
        </span>
      )}
    </>
  )
}
